import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDate } from "@/lib/format";
import { useSession } from "@/lib/auth-client";
import { api, type OrgInvitationRow, type OrgMemberRow, type OrgRole } from "@/lib/api";
import { useOrgRole } from "@/lib/use-org-role";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  Crown,
  MoreHorizontal,
  UserPlus,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { InviteDialog } from "@/components/InviteDialog";
import { RoleMenuItem } from "@/components/OrgRoleParts";
import {
  ASSIGNABLE_ORG_ROLES,
  ORG_ROLE_LABEL,
  ORG_ROLE_TAGLINE,
} from "@/lib/org-roles";

function roleBadgeVariant(role: string) {
  if (role === "owner") return "default" as const;
  if (role === "admin") return "secondary" as const;
  return "outline" as const;
}

function canActOn(myRole: OrgRole | undefined, theirRole: string): boolean {
  if (!myRole || myRole === "member" || myRole === "billing_admin") return false;
  if (myRole === "owner") return true;
  // admin can act on members and billing admins — never on owner or other admins
  return theirRole === "member" || theirRole === "billing_admin";
}

// ─── Types ────────────────────────────────────────────────────────────────────
// Unified row used by the merged table.
type Row =
  | {
      kind: "member";
      key: string;
      memberId: string;
      userId: string;
      name: string;
      email: string;
      role: string;
      isSelf: boolean;
      createdAt: string | Date;
    }
  | {
      kind: "invitation";
      key: string;
      invitationId: string;
      email: string;
      role: string;
      expiresAt: string | Date;
      emailError?: string | null;
    };

type Filter = "admins" | "readonly" | "invited";

const FILTERS: Filter[] = ["admins", "readonly", "invited"];

const FILTER_LABEL: Record<Filter, string> = {
  admins: "Admins",
  readonly: "Read-only",
  invited: "Invited",
};

const FILTER_EMPTY: Record<Filter, string> = {
  admins: "No admins yet.",
  readonly: "No read-only people.",
  invited: "No pending invites.",
};

const ADMIN_ROLES = new Set(["owner", "admin", "billing_admin"]);

function filterOf(row: Row): Filter {
  if (row.kind === "invitation") return "invited";
  return ADMIN_ROLES.has(row.role) ? "admins" : "readonly";
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function MembersPage() {
  useDocumentTitle("Admins");
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>("admins");
  const [confirmTransfer, setConfirmTransfer] = useState<OrgMemberRow | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ userId: string; name: string } | null>(null);
  const [confirmCancelInvite, setConfirmCancelInvite] = useState<{ invitationId: string; email: string } | null>(null);

  const { orgId, org, myRole, isLoading, isError, error } = useOrgRole();

  const cancelInvite = useMutation({
    mutationFn: (invitationId: string) => api.org.cancelInvitation(invitationId),
    onSuccess: () => {
      setConfirmCancelInvite(null);
      queryClient.invalidateQueries({ queryKey: ["org-full", orgId] });
    },
  });

  const resendInvite = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: string }) =>
      api.org.invite({ email, role, resend: true }),
    onSuccess: () => {
      toast.success("Invitation resent");
      queryClient.invalidateQueries({ queryKey: ["org-full", orgId] });
    },
  });

  const updateRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: OrgRole }) =>
      api.org.updateMemberRole(userId, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["org-full", orgId] }),
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.org.removeMember(userId),
    onSuccess: () => {
      setConfirmRemove(null);
      queryClient.invalidateQueries({ queryKey: ["org-full", orgId] });
    },
  });

  if (!orgId) {
    return (
      <div className="p-6 text-sm text-muted-foreground">No active organization.</div>
    );
  }

  const members: OrgMemberRow[] = org?.members ?? [];
  const invitations: OrgInvitationRow[] = (org?.invitations ?? []).filter(
    (i) => i.status === "pending",
  );
  const canInvite = myRole === "owner" || myRole === "admin";

  // One flat list of people: the owner first, then everyone by name. The
  // filter chips pick which slice shows; roles and invites are not groups.
  const memberRows: Row[] = members.map((m) => ({
    kind: "member",
    key: `m:${m.id}`,
    memberId: m.id,
    userId: m.user.id,
    name: m.user.name,
    email: m.user.email,
    role: m.role,
    isSelf: m.user.id === session?.user.id,
    createdAt: m.createdAt,
  }));

  const invitationRows: Row[] = invitations.map((inv) => ({
    kind: "invitation",
    key: `i:${inv.id}`,
    invitationId: inv.id,
    email: inv.email,
    role: inv.role,
    expiresAt: inv.expiresAt,
    emailError: inv.emailError,
  }));

  const sortName = (row: Row) => (row.kind === "member" ? row.name || row.email : row.email);
  const isOwner = (row: Row) => row.kind === "member" && row.role === "owner";
  const rows: Row[] = [...memberRows, ...invitationRows].sort(
    (a, b) =>
      Number(isOwner(b)) - Number(isOwner(a)) ||
      sortName(a).localeCompare(sortName(b), undefined, { sensitivity: "base" }),
  );
  const counts: Record<Filter, number> = { admins: 0, readonly: 0, invited: 0 };
  for (const row of rows) counts[filterOf(row)] += 1;
  const visible = rows.filter((row) => filterOf(row) === filter);

  return (
    <div className="p-6 space-y-8">
      <PageHeader
        title="Admins"
        subtitle="Who can manage this organization, and everyone else who can see it."
      />

      {/* People + invitations table. Convention: any "create / add" CTA for
          a table sits at its top-right — not in the page-level header. */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {org && (
            <div className="flex flex-wrap gap-1" role="group" aria-label="Show">
              {FILTERS.map((f) => (
                <Button
                  key={f}
                  size="sm"
                  variant={filter === f ? "secondary" : "ghost"}
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                  className="tabular-nums"
                >
                  {FILTER_LABEL[f]}
                  <span className="text-muted-foreground">{counts[f]}</span>
                </Button>
              ))}
            </div>
          )}
          {canInvite && (
            <Button className="ml-auto" onClick={() => setInviteOpen(true)}>
              <UserPlus className="size-4" />
              Invite admin
            </Button>
          )}
        </div>
        {org && filter === "readonly" && (
          <p className="text-sm text-muted-foreground">{ORG_ROLE_TAGLINE.member}</p>
        )}
        {isLoading ? (
          <LoadingIndicator />
        ) : isError || !org ? (
          <ErrorComponent error={error ?? "Failed to load admins"} />
        ) : visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">{FILTER_EMPTY[filter]}</p>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="w-[180px]">Role</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((row) => {
                  if (row.kind === "member") {
                    const showActions =
                      !row.isSelf && canActOn(myRole, row.role);
                    const canEditRole =
                      showActions && row.role !== "owner";
                    return (
                      <TableRow key={row.key}>
                        <TableCell>
                          <div className="text-sm font-medium">
                            {row.name}
                            {row.isSelf && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (you)
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {row.email}
                            {" · "}
                            Joined {formatDate(row.createdAt)}
                          </div>
                        </TableCell>
                        <TableCell>
                          {canEditRole ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="font-normal justify-between w-[140px]"
                                  disabled={updateRole.isPending}
                                >
                                  {ORG_ROLE_LABEL[row.role as OrgRole] ?? row.role}
                                  <ChevronDown className="size-3.5 opacity-50" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="w-[20rem]">
                                {ASSIGNABLE_ORG_ROLES.map((r) => (
                                  <RoleMenuItem
                                    key={r}
                                    role={r}
                                    selected={row.role === r}
                                    disabled={r === row.role}
                                    onSelect={() =>
                                      updateRole.mutate({
                                        userId: row.userId,
                                        role: r,
                                      })
                                    }
                                  />
                                ))}
                                {myRole === "owner" && row.role !== "owner" && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => {
                                        const m = members.find(
                                          (m) => m.id === row.memberId,
                                        );
                                        if (m) setConfirmTransfer(m);
                                      }}
                                      className="items-start gap-2 py-2"
                                    >
                                      <Crown className="size-4 mt-0.5 shrink-0 text-muted-foreground" />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium">
                                          Transfer ownership
                                        </div>
                                        <p className="text-xs text-muted-foreground leading-snug whitespace-normal">
                                          {ORG_ROLE_TAGLINE.owner}
                                        </p>
                                      </div>
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <Badge variant={roleBadgeVariant(row.role)}>
                              {ORG_ROLE_LABEL[row.role as OrgRole] ?? row.role}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status="active" />
                        </TableCell>
                        <TableCell>
                          {showActions && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                >
                                  <MoreHorizontal className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel className="text-xs text-muted-foreground">
                                  Manage
                                </DropdownMenuLabel>
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setConfirmRemove({ userId: row.userId, name: row.name || row.email })}
                                >
                                  Remove from org
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // invitation row
                  return (
                    <TableRow key={row.key}>
                      <TableCell>
                        <div className="text-sm font-medium">{row.email}</div>
                        <div className="text-xs text-muted-foreground">
                          Expires {formatDate(row.expiresAt)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={roleBadgeVariant(row.role)}>
                          {ORG_ROLE_LABEL[row.role as OrgRole] ?? row.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={row.emailError ? "email_failed" : "invited"}
                          errorMessage={row.emailError}
                        />
                      </TableCell>
                      <TableCell>
                        {canInvite && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={resendInvite.isPending}
                              >
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  const link = `${window.location.origin}/accept-invitation/${row.invitationId}`;
                                  navigator.clipboard.writeText(link);
                                  toast.success("Invite link copied");
                                }}
                              >
                                Copy invite link
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  resendInvite.mutate({
                                    email: row.email,
                                    role: row.role,
                                  })
                                }
                              >
                                Resend invitation
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setConfirmCancelInvite({ invitationId: row.invitationId, email: row.email })}
                              >
                                Cancel invitation
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {(updateRole.isError || removeMember.isError || cancelInvite.isError || resendInvite.isError) && (
          <InlineError
            error={updateRole.error || removeMember.error || cancelInvite.error || resendInvite.error}
          />
        )}
      </section>

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} organizationId={orgId} />

      <Dialog open={!!confirmRemove} onOpenChange={(v) => !v && setConfirmRemove(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Remove {confirmRemove?.name} from {org?.name || "this organization"}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">They will lose access immediately.</p>
            {removeMember.isError && <InlineError error={removeMember.error} />}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmRemove(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                loading={removeMember.isPending}
                onClick={() => {
                  if (!confirmRemove) return;
                  removeMember.mutate(confirmRemove.userId);
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmCancelInvite} onOpenChange={(v) => !v && setConfirmCancelInvite(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel invitation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Cancel the invitation to <strong>{confirmCancelInvite?.email}</strong>? The invite link will no longer work.
            </p>
            {cancelInvite.isError && <InlineError error={cancelInvite.error} />}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmCancelInvite(null)}>
                Keep invitation
              </Button>
              <Button
                variant="destructive"
                loading={cancelInvite.isPending}
                onClick={() => {
                  if (!confirmCancelInvite) return;
                  cancelInvite.mutate(confirmCancelInvite.invitationId);
                }}
              >
                Cancel invitation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmTransfer} onOpenChange={(v) => !v && setConfirmTransfer(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer ownership</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              Make <strong>{confirmTransfer?.user.name}</strong> the owner of this organization.
              You will be demoted to admin and lose owner-only permissions.
            </p>
            {updateRole.isError && <InlineError error={updateRole.error} />}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmTransfer(null)}>
                Cancel
              </Button>
              <Button
                loading={updateRole.isPending}
                onClick={() => {
                  if (!confirmTransfer) return;
                  updateRole.mutate(
                    { userId: confirmTransfer.user.id, role: "owner" },
                    { onSuccess: () => setConfirmTransfer(null) },
                  );
                }}
              >
                Transfer ownership
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
